/**
 * REQUEST INJECT SALDO v2.0 - Form untuk Penjaga Loket
 * Fitur request inject saldo dengan kategori
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Wallet,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Upload,
  X,
  RefreshCw,
  AlertCircle,
  DollarSign,
  AlertTriangle,
  Monitor,
  Edit3
} from 'lucide-react';

const RequestInjectSaldo = ({ api, user }) => {
  const [loketSaldo, setLoketSaldo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // KATEGORI options
  const kategoriOptions = [
    {
      value: 'SALDO_MAU_ABIS',
      label: 'Saldo Mau Habis',
      icon: '⚠️',
      color: 'yellow',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-400',
      textColor: 'text-yellow-700',
      description: 'Saldo hampir habis, tapi masih bisa transaksi'
    },
    {
      value: 'SALDO_HABIS',
      label: 'Saldo Habis',
      icon: '❌',
      color: 'red',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-400',
      textColor: 'text-red-700',
      description: 'Saldo sudah habis, tidak bisa transaksi'
    },
    {
      value: 'REVISI_SISTEM',
      label: 'Revisi Sistem',
      icon: '🖥️',
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-400',
      textColor: 'text-blue-700',
      description: 'Ada kesalahan sistem, butuh inject untuk koreksi'
    },
    {
      value: 'REVISI_INPUT',
      label: 'Revisi Input',
      icon: '✏️',
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-400',
      textColor: 'text-purple-700',
      description: 'Ada kesalahan input, butuh inject untuk koreksi'
    }
  ];

  // Form state
  const [formData, setFormData] = useState({
    kategori: '',
    bank_code: 'BRIS',
    jumlah_request: '',
    alasan: '',
    // SALDO_MAU_ABIS
    estimasi_trx_tersisa: '',
    // REVISI_SISTEM
    error_code: '',
    ticket_id: '',
    laporan_shift_id: '',
    // REVISI_INPUT
    slip_id: '',
    slip_id: '',
    // General
    keterangan_lanjutan: ''
  });

  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Request history
  const [requestHistory, setRequestHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadLoketData();
    loadRequestHistory();
  }, []);

  const loadLoketData = async () => {
    setLoading(true);
    try {
      const banks = await api.get('/api/inject-saldo/lokets');
      const myLoket = banks.find(l => l.id === user?.loket_id);
      if (myLoket) {
        setLoketSaldo(myLoket.banks || []);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRequestHistory = async () => {
    try {
      const history = await api.get('/api/inject-saldo/requests/my');
      setRequestHistory(history);
    } catch (err) {
      console.error('Error loading history:', err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File terlalu besar. Maksimal 5MB');
        return;
      }

      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Build payload based on kategori
      const payload = {
        loket_id: user?.loket_id,
        bank_code: formData.bank_code,
        jumlah_request: parseFloat(formData.jumlah_request),
        kategori: formData.kategori,
        alasan: formData.alasan,
        keterangan_lanjutan: formData.keterangan_lanjutan
      };

      // Add category-specific fields
      if (formData.kategori === 'SALDO_MAU_ABIS' && formData.estimasi_trx_tersisa) {
        payload.estimasi_trx_tersisa = parseInt(formData.estimasi_trx_tersisa);
      }

      if (formData.kategori === 'REVISI_SISTEM') {
        if (formData.error_code) payload.error_code = formData.error_code;
        if (formData.ticket_id) payload.ticket_id = formData.ticket_id;
        if (formData.laporan_shift_id) payload.laporan_shift_id = formData.laporan_shift_id;
      }

      if (formData.kategori === 'REVISI_INPUT') {
        if (formData.slip_id) payload.slip_id = formData.slip_id;
        if (formData.laporan_shift_id) payload.laporan_shift_id = formData.laporan_shift_id;
      }

      // Add file as base64 if exists
      if (filePreview) {
        payload.bukti = filePreview;
        payload.nama_file_bukti = selectedFile?.name;
      }

      await api.post('/api/inject-saldo/request', payload);

      setSuccess('Request inject saldo berhasil dikirim! Menunggu persetujuan Manager.');
      resetForm();
      await loadRequestHistory();

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message || 'Gagal mengirim request');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      kategori: '',
      bank_code: 'BRIS',
      jumlah_request: '',
      alasan: '',
      estimasi_trx_tersisa: '',
      error_code: '',
      ticket_id: '',
      slip_id: '',
      laporan_shift_id: '',
      keterangan_lanjutan: ''
    });
    removeFile();
  };

  const formatRupiah = (num) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(num);
  };

  const getCurrentSaldo = () => {
    const bank = loketSaldo.find(b => b.bank_code === formData.bank_code);
    return bank?.saldo || 0;
  };

  const getSelectedKategori = () => {
    return kategoriOptions.find(k => k.value === formData.kategori);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', label: '⏳ Menunggu' },
      'COMPLETED': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: '✓ Selesai' },
      'REJECTED': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: '✗ Ditolak' }
    };
    const s = badges[status] || badges['PENDING'];
    return (
      <span className={`px-2 py-1 text-xs rounded-full border ${s.bg} ${s.text} ${s.border}`}>
        {s.label}
      </span>
    );
  };

  const getKategoriBadge = (kategori) => {
    const kat = kategoriOptions.find(k => k.value === kategori);
    if (!kat) return null;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${kat.bgColor} ${kat.textColor}`}>
        {kat.icon} {kat.label}
      </span>
    );
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return `${diff} detik lalu`;
    if (diff < 3600) return `${Math.floor(diff / 60)} menit lalu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
    return new Date(dateString).toLocaleDateString('id-ID');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="animate-spin mr-2" />
        <span>Memuat data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="text-purple-600" />
            Request Inject Saldo
          </h1>
          <p className="text-gray-500 mt-1">
            Ajukan request inject saldo dengan kategori yang sesuai
          </p>
        </div>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            showHistory
              ? 'bg-purple-100 text-purple-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {showHistory ? 'Form Request' : 'Riwayat Request'}
        </button>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded flex items-center">
          <CheckCircle className="text-green-500 mr-2" size={20} />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded flex items-center">
          <AlertCircle className="text-red-500 mr-2" size={20} />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Saldo Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Saldo Bank Saat Ini</h2>
        <div className="grid grid-cols-2 gap-4">
          {loketSaldo.map(bank => (
            <div
              key={bank.bank_code}
              className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                formData.bank_code === bank.bank_code
                  ? bank.bank_code === 'BRIS'
                    ? 'border-green-500 bg-green-50'
                    : 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setFormData({ ...formData, bank_code: bank.bank_code })}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">
                  {bank.bank_code === 'BRIS' ? '💚 BSI' : '💙 Bank Mandiri'}
                </span>
                {formData.bank_code === bank.bank_code && (
                  <span className="text-xs text-gray-500">Selected</span>
                )}
              </div>
              <p className={`text-2xl font-bold mt-2 ${
                bank.saldo < 500000 ? 'text-red-600' : bank.saldo < 1000000 ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {formatRupiah(bank.saldo)}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Request Form or History */}
      {showHistory ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="text-gray-600" />
            Riwayat Request
          </h2>

          {requestHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="mx-auto mb-2 text-gray-400" size={48} />
              <p>Belum ada request inject saldo</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requestHistory.map(req => (
                <div key={req.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {getKategoriBadge(req.kategori)}
                        {getStatusBadge(req.status)}
                        <span className="text-sm text-gray-400">
                          {req.bank_code === 'BRIS' ? '💚 BSI' : '💙 Mandiri'}
                        </span>
                      </div>
                      <p className="text-xl font-bold text-gray-900">
                        {formatRupiah(req.jumlah_request)}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">{req.alasan}</p>
                      {req.keterangan_lanjutan && (
                        <p className="text-sm text-gray-500 mt-1">{req.keterangan_lanjutan}</p>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      {timeAgo(req.created_at)}
                    </div>
                  </div>
                  {req.status === 'REJECTED' && req.rejection_note && (
                    <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm font-medium text-red-700">Alasan Penolakan:</p>
                      <p className="text-sm text-red-600">{req.rejection_note}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Send className="text-purple-600" />
            Form Request Inject Saldo
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* STEP 1: Kategori */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                1️⃣ PILIH KATEGORI *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {kategoriOptions.map(kat => (
                  <button
                    key={kat.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, kategori: kat.value })}
                    className={`p-4 border-2 rounded-lg text-left transition-all ${
                      formData.kategori === kat.value
                        ? `${kat.borderColor} ${kat.bgColor}`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{kat.icon}</span>
                      <div>
                        <p className={`font-semibold ${formData.kategori === kat.value ? kat.textColor : 'text-gray-900'}`}>
                          {kat.label}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{kat.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* STEP 2: Bank Selection */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                2️⃣ PILIH BANK *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, bank_code: 'BRIS' })}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    formData.bank_code === 'BRIS'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <div className="font-semibold text-lg">💚 BSI</div>
                  <div className="text-sm mt-1">{formatRupiah(loketSaldo.find(b => b.bank_code === 'BRIS')?.saldo || 0)}</div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, bank_code: 'MANDIRI' })}
                  className={`p-4 border-2 rounded-lg text-center transition-all ${
                    formData.bank_code === 'MANDIRI'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                >
                  <div className="font-semibold text-lg">💙 Bank Mandiri</div>
                  <div className="text-sm mt-1">{formatRupiah(loketSaldo.find(b => b.bank_code === 'MANDIRI')?.saldo || 0)}</div>
                </button>
              </div>
            </div>

            {/* STEP 3: Detail sesuai Kategori */}
            {formData.kategori && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  3️⃣ DETAIL ({kategoriOptions.find(k => k.value === formData.kategori)?.label}) *
                </label>
                <div className="p-4 border border-gray-200 rounded-lg space-y-4 bg-gray-50">
                  {/* SALDO_MAU_ABIS */}
                  {formData.kategori === 'SALDO_MAU_ABIS' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Estimasi Trx Tersisa *
                        </label>
                        <input
                          type="number"
                          value={formData.estimasi_trx_tersisa}
                          onChange={(e) => setFormData({ ...formData, estimasi_trx_tersisa: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="Contoh: 50"
                          min="1"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Jumlah Request (Rp) *
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="number"
                            value={formData.jumlah_request}
                            onChange={(e) => setFormData({ ...formData, jumlah_request: e.target.value })}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="Masukkan jumlah request"
                            min="10000"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Alasan / Keterangan *
                        </label>
                        <textarea
                          value={formData.alasan}
                          onChange={(e) => setFormData({ ...formData, alasan: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          rows="3"
                          placeholder="Jelaskan kondisi saldo dan alasan request..."
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* SALDO_HABIS */}
                  {formData.kategori === 'SALDO_HABIS' && (
                    <>
                      <div className={`p-3 rounded-lg ${getCurrentSaldo() < 100000 ? 'bg-red-100' : 'bg-yellow-100'}`}>
                        <p className={`text-sm font-medium ${getCurrentSaldo() < 100000 ? 'text-red-700' : 'text-yellow-700'}`}>
                          💰 Saldo saat ini: {formatRupiah(getCurrentSaldo())}
                          {getCurrentSaldo() < 100000 && ' (SANGAT KRITIS!)'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Jumlah Request (Rp) *
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="number"
                            value={formData.jumlah_request}
                            onChange={(e) => setFormData({ ...formData, jumlah_request: e.target.value })}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="Masukkan jumlah request"
                            min="10000"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Alasan / Keterangan *
                        </label>
                        <textarea
                          value={formData.alasan}
                          onChange={(e) => setFormData({ ...formData, alasan: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          rows="3"
                          placeholder="Jelaskan alasan saldo habis dan kebutuhan..."
                          required
                        />
                      </div>
                    </>
                  )}

                  {/* REVISI_SISTEM */}
                  {formData.kategori === 'REVISI_SISTEM' && (
                    <>
                      <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                        <Monitor className="text-blue-500 flex-shrink-0 mt-0.5" size={18} />
                        <p className="text-sm text-blue-700">
                          Koreksi kesalahan sistem. Sampaikan error code atau ticket IT jika ada.
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Error Code / Ticket ID
                          </label>
                          <input
                            type="text"
                            value={formData.error_code}
                            onChange={(e) => setFormData({ ...formData, error_code: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="Contoh: ERR-001"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ID Laporan Shift
                          </label>
                          <input
                            type="text"
                            value={formData.laporan_shift_id}
                            onChange={(e) => setFormData({ ...formData, laporan_shift_id: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="Contoh: LP-2024-001"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Deskripsi Error / Masalah *
                        </label>
                        <textarea
                          value={formData.alasan}
                          onChange={(e) => setFormData({ ...formData, alasan: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          rows="3"
                          placeholder="Jelaskan kesalahan sistem yang terjadi..."
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Jumlah Koreksi (Rp) *
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="number"
                            value={formData.jumlah_request}
                            onChange={(e) => setFormData({ ...formData, jumlah_request: e.target.value })}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="Masukkan jumlah koreksi"
                            min="1"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* REVISI_INPUT */}
                  {formData.kategori === 'REVISI_INPUT' && (
                    <>
                      <div className="p-3 bg-purple-50 rounded-lg flex items-start gap-2">
                        <Edit3 className="text-purple-500 flex-shrink-0 mt-0.5" size={18} />
                        <p className="text-sm text-purple-700">
                          Koreksi kesalahan input. Sampaikan slip/laporan terkait untuk verifikasi.
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          ID Slip / Laporan Salah
                        </label>
                        <input
                          type="text"
                          value={formData.slip_id}
                          onChange={(e) => setFormData({ ...formData, slip_id: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="Contoh: SLIP-2024-050"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Kesalahan yang Terjadi *
                        </label>
                        <textarea
                          value={formData.alasan}
                          onChange={(e) => setFormData({ ...formData, alasan: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          rows="3"
                          placeholder="Jelaskan kesalahan input yang terjadi..."
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Jumlah Koreksi (Rp) *
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                          <input
                            type="number"
                            value={formData.jumlah_request}
                            onChange={(e) => setFormData({ ...formData, jumlah_request: e.target.value })}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="Masukkan jumlah koreksi"
                            min="1"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* STEP 4: Bukti (Optional) */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3">
                4️⃣ BUKTI (OPSIONAL)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                {filePreview ? (
                  <div className="space-y-3">
                    <img
                      src={filePreview}
                      alt="Preview"
                      className="max-h-48 mx-auto rounded-lg"
                    />
                    <p className="text-sm text-gray-500">{selectedFile?.name}</p>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Hapus file
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                    <p className="text-sm text-gray-500 mb-3">
                      Upload bukti screenshot / slip / error log
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      id="bukti-upload"
                    />
                    <label
                      htmlFor="bukti-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200"
                    >
                      <Upload size={16} />
                      Pilih File
                    </label>
                    <p className="text-xs text-gray-400 mt-2">
                      Format: JPG, PNG, PDF (Max: 5MB)
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Warning */}
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-yellow-500 flex-shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-yellow-700">
                  <p className="font-medium">Perhatian:</p>
                  <p>Pastikan semua informasi yang diisi sudah benar. Request akan ditinjau oleh Manager.</p>
                  {['REVISI_SISTEM', 'REVISI_INPUT'].includes(formData.kategori) && (
                    <p className="mt-1">Disarankan untuk upload bukti untuk mempercepat proses approval.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || !formData.kategori || !formData.jumlah_request || !formData.alasan}
              className={`w-full py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 ${
                submitting || !formData.kategori || !formData.jumlah_request || !formData.alasan
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {submitting ? (
                <>
                  <RefreshCw className="animate-spin" size={20} />
                  Mengirim Request...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Kirim Request Inject Saldo
                </>
              )}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default RequestInjectSaldo;
